import { ClassNames } from "@emotion/core";
import { usePrevious } from "@react-hookz/web";
import { getClientId } from "@smartsheet/lodestar-utils";
import React, {
    forwardRef,
    MouseEventHandler,
    useState,
    useEffect,
    Ref,
    ButtonHTMLAttributes,
    useCallback,
    useMemo,
} from "react";
import { InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { useFocusMode } from "../../../focusMode";
import { TextInputProps } from "../../../textinput";
import { DatePickerFormat, DatePickerDate, INPUT_REGEX, DatePickerComponentOverrideProps } from "../../types";
import { DatePickerTextInputStyled } from "../styled/DatePickerTextInputStyled";
import { DatePickerTextInputWrap } from "../styled/DatePickerTextInputWrap";
import {
    validateInput,
    dateToInputString,
    inputStringToDate,
    inputHelper,
    isDateBetweenDates,
    DatePickerInputValidationResult,
} from "../utils";
import { DatePickerInputCalendarButton } from "./DatePickerInputCalendarButton";
import { DatePickerTextInputPlaceholder } from "./DatePickerTextInputPlaceholder";

export interface DatePickerInputBaseProps
    extends InputFieldBaseProps,
        Omit<
            React.InputHTMLAttributes<HTMLInputElement>,
            "defaultValue" | "disabled" | "onChange" | "readOnly" | "style" | "type" | "value"
        > {
    /**
     * Any extra props for the calendar button. These can change depending on if
     * we're using the single or range picker.
     */
    calendarButtonProps: ButtonHTMLAttributes<HTMLButtonElement> & {
        ref: Ref<HTMLButtonElement>;
    };

    /**
     * The Date format displayed to the user in the input.
     */
    format: DatePickerFormat;

    /**
     * Callback for date change.
     * Will be called when a date valid for the given format is either
     * typed in or selected via the dialog.
     *
     * Can be called with null if the user deletes all content.
     */
    onChange: (date: DatePickerDate | null) => void;

    /**
     * Called when the date within this input does not satisfy its validation requirements.
     */
    onValidationChange: (newValidation: DatePickerInputValidationResult, value?: string) => void;

    /**
     * The current date, if there is one. This is assumed to be in UTC and all date
     * manipulations will assume UTC. It should not have any time associated with
     * it i.e. it's always midnight in DatePicker land.
     */
    value: DatePickerDate | null;

    /**
     * A classname to be applied to the root element of this component.
     */
    className?: string;

    /**
     * A `clientId` prop is provided for specified elements, which is a unique
     * string that appears as a data attribute `data-client-id` in the rendered code,
     * serving as a hook for automated tests
     */
    clientId?: string;

    /**
     * If provided, called for each day in the current calendar viewport to check
     * if it should be selectable or not.
     *
     * Works alongside minDate/maxDate as well.
     */
    dayIsDisabled?: (date: DatePickerDate) => boolean;

    /**
     * Flag to determine if the input is compact or responsive
     * defaults to true
     */
    isCompact?: boolean;

    /**
     * Flag to determine if the input is disabled.
     */
    isDisabled?: boolean;

    /**
     * Flag to determine if the input is readOnly.
     */
    isReadOnly?: boolean;

    /**
     * Shows an error if field is emptied and is required.
     */
    isRequired?: boolean;

    /**
     * The maximum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    maxDate?: DatePickerDate;

    /**
     * The minimum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    minDate?: DatePickerDate;

    /**
     * Internal component overrides
     */
    overrides?: DatePickerComponentOverrideProps;

    /**
     * An id to be passed into the placeholder element. This can be used for many things,
     * such as adding an ID to reference the placeholder content in an input's aria-labelledby.
     */
    placeholderId?: string;

    /**
     * an optional presentational value to be displayed in the input field.
     * This is useful for displaying a different format than the one used
     * to validate or parse the date. See our DatePickerPresentationalFormatStory
     * for an example usage.
     */
    presentationalValue?: string;

    variant?: TextInputProps["variant"];
}

export const AtomicDatePickerInput = forwardRef<HTMLInputElement, DatePickerInputBaseProps>(
    (
        {
            calendarButtonProps,
            className,
            clientId,
            dayIsDisabled,
            format,
            isDisabled = false,
            isReadOnly = false,
            maxDate,
            minDate,
            isRequired,
            onBlur,
            onChange,
            onValidationChange,
            value,
            presentationalValue,
            isCompact = true,
            inputFieldHeight,
            placeholderId,
            overrides,
            ...rest
        },
        ref
    ) => {
        const initialValue: string = value ? dateToInputString(value, format) : "";

        const [date, setDate] = useState<string>(initialValue);
        const [dateValid, setDateValid] = useState(true);
        const [placeholder, setPlaceholder] = useState<string>(initialValue);
        const [hasBlurred, setHasBlurred] = useState(false);
        const [isFocused, setIsFocused] = useState(false);
        const { focusMode } = useFocusMode();

        const validationState = overrides?.textInput?.validationState || (dateValid ? undefined : "error");

        const onDateChange = (val: string) => {
            /**
             * Here we're making sure what you typed is a valid character
             * e.g. numbers and delimiters
             */
            let newValue = val.replace(INPUT_REGEX, "");

            if (date.length <= newValue.length) {
                newValue = inputHelper(newValue, format);
            }

            setPlaceholder(newValue);

            const validationResult = validateInput(newValue, format, minDate, maxDate, dayIsDisabled, isRequired);
            const isValid = validationResult === "isValid";

            if (hasBlurred || newValue.length === format.length || (newValue.length === 0 && isRequired)) {
                onValidationChange(validationResult, newValue);
                setDateValid(isValid);
            }

            if (isValid) {
                onChange(inputStringToDate(newValue, format));
                setDate(newValue);
            } else if (newValue.length === 0) {
                setDate(newValue);
                onChange(null);
            } else {
                /**
                 * handles deletion of characters first (old value vs new value length),
                 * then moves to checking for stuff we can auto-insert
                 */
                if (date.length > newValue.length) {
                    setDate(newValue);
                } else {
                    setPlaceholder(newValue);
                    setDate(newValue);
                }
            }
        };

        const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
            setHasBlurred(true);
            setIsFocused(false);
            if (date.trim().length > 0 || isRequired) {
                const validationResult = validateInput(date, format, minDate, maxDate, dayIsDisabled, isRequired);
                onValidationChange(validationResult, date);
                setDateValid(validationResult === "isValid");
            } else {
                // clear error messages if the input is empty
                setDateValid(true);
                onValidationChange("isValid");
            }
            onBlur?.(e);
        };

        const onCalendarClick: MouseEventHandler<HTMLButtonElement> = (e) => {
            e.stopPropagation();
            calendarButtonProps.onClick?.(e);
        };

        const handleSomeValidChanges = useCallback(
            (validationResult: DatePickerInputValidationResult, dateString: string) => {
                if (validationResult === "isValid") {
                    setDateValid(true);
                    onValidationChange("isValid");
                } else {
                    setDateValid(false);
                    onValidationChange(validationResult, dateString);
                }
            },
            [onValidationChange]
        );

        const prevValue = usePrevious(value);
        useEffect(() => {
            if (prevValue !== value) {
                if (value) {
                    const dateString = dateToInputString(value, format);
                    const validationResult = isDateBetweenDates(value, minDate, maxDate);
                    setPlaceholder(dateString);
                    setDate(dateString);
                    handleSomeValidChanges(validationResult, dateString);
                } else {
                    setDate("");
                    setDateValid(true);
                    onValidationChange("isValid");
                    setPlaceholder("");
                }
            }
        }, [format, maxDate, minDate, onValidationChange, prevValue, value, handleSomeValidChanges]);

        useEffect(() => {
            if (prevValue !== undefined && prevValue !== value && (value || isRequired)) {
                const dateString = value ? dateToInputString(value, format) : "";
                const validationResult = validateInput(dateString, format, minDate, maxDate, dayIsDisabled, isRequired);
                handleSomeValidChanges(validationResult, dateString);
            }
        }, [handleSomeValidChanges, prevValue, value, format, minDate, maxDate, dayIsDisabled, isRequired, date]);

        const prevFormat = usePrevious(format);
        useEffect(() => {
            if (prevFormat && prevFormat !== format && value) {
                const date = dateToInputString(value, format);
                setDate(date);
                setPlaceholder(date);
            }
        }, [format, prevFormat, value]);

        const inputValue = useMemo(() => {
            if (presentationalValue && !isFocused) {
                return presentationalValue;
            }
            return date;
        }, [presentationalValue, isFocused, date]);

        const shouldShowPlaceholder = useMemo(() => {
            if (isFocused && inputValue.length === 0) return true;
            if (presentationalValue) return false;
            return true;
        }, [isFocused, inputValue, presentationalValue]);

        return (
            <ClassNames>
                {({ cx }) => (
                    <DatePickerTextInputWrap>
                        {shouldShowPlaceholder && (
                            <DatePickerTextInputPlaceholder
                                clientId={clientId}
                                format={format}
                                value={placeholder}
                                validationState={validationState}
                                focusMode={focusMode}
                                isFocused={isFocused}
                                inputFieldHeight={inputFieldHeight}
                                variant={overrides?.textInput?.variant}
                                id={placeholderId}
                            />
                        )}
                        <DatePickerTextInputStyled
                            inputFieldHeight={inputFieldHeight}
                            className={cx(className, { "compact-dp-input": isCompact })}
                            clientId={getClientId(clientId, "dp-input-input")}
                            value={inputValue}
                            onChange={onDateChange}
                            onBlur={onBlurHandler}
                            onFocus={() => setIsFocused(true)}
                            contentAfter={
                                <DatePickerInputCalendarButton
                                    inputFieldHeight={inputFieldHeight}
                                    {...calendarButtonProps}
                                    onClick={onCalendarClick}
                                    isDisabled={isDisabled}
                                    isReadOnly={isReadOnly}
                                    clientId={clientId}
                                />
                            }
                            validationState={validationState}
                            readOnly={isReadOnly}
                            isDisabled={isDisabled}
                            ref={ref}
                            {...rest}
                            {...overrides?.textInput}
                        />
                    </DatePickerTextInputWrap>
                )}
            </ClassNames>
        );
    }
);

AtomicDatePickerInput.displayName = "AtomicDatePickerInput";
